import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type GalleryViewerProps = {
    urls: string[];
    startIndex?: number;
    title?: string;
    onClose: () => void;
};

function clampIndex(value: number, len: number): number {
    if (len <= 0) return 0;
    const v: number = value % len;
    return v < 0 ? v + len : v;
}

function prettyMediaLabel(fileOrUrl: string): string {
    const rawName = (() => {
        const s = fileOrUrl.split("?")[0] ?? fileOrUrl;
        const parts = s.split("/");
        return parts[parts.length - 1] ?? s;
    })();

    const noExt = rawName.replace(/\.[^/.]+$/, "");
    const spaced = noExt.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();

    const titled = spaced
        .split(" ")
        .filter(Boolean)
        .map((w) => w.slice(0, 1).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");

    return titled || noExt || rawName;
}

type LabelInset = {
    left: number;
    top: number;
};

export default function GalleryViewer(props: GalleryViewerProps): JSX.Element {
    const { urls, title, onClose } = props;

    const initialIndex: number = useMemo(() => {
        const idx: number =
            typeof props.startIndex === "number" ? props.startIndex : 0;
        return clampIndex(idx, urls.length);
    }, [props.startIndex, urls.length]);

    const [index, setIndex] = useState<number>(initialIndex);
    const [zoom, setZoom] = useState<number>(1);
    const [drag, setDrag] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState<boolean>(false);

    const overlayRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLDivElement | null>(null);

    const mediaBoxRef = useRef<HTMLDivElement | null>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);

    const [headerHeight, setHeaderHeight] = useState<number>(0);

    const [labelInset, setLabelInset] = useState<LabelInset>({
        left: 14,
        top: 14,
    });

    const canNavigate: boolean = urls.length > 1;
    const currentUrl: string | null = urls.length
        ? urls[clampIndex(index, urls.length)]
        : null;

    const currentLabel: string = useMemo(() => {
        if (!currentUrl) return "";
        return prettyMediaLabel(currentUrl);
    }, [currentUrl]);

    const goTo = useCallback(
        (nextIndex: number) => {
            if (!urls.length) return;
            setIndex(clampIndex(nextIndex, urls.length));
            setZoom(1);
            setDrag({ x: 0, y: 0 });
        },
        [urls.length],
    );

    const prev = useCallback(() => {
        if (!canNavigate) return;
        goTo(index - 1);
    }, [canNavigate, goTo, index]);

    const next = useCallback(() => {
        if (!canNavigate) return;
        goTo(index + 1);
    }, [canNavigate, goTo, index]);

    useEffect(() => {
        setIndex(initialIndex);
        setZoom(1);
        setDrag({ x: 0, y: 0 });
    }, [initialIndex]);

    useEffect(() => {
        const prevOverflow: string = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                prev();
                return;
            }
            if (e.key === "ArrowRight") {
                e.preventDefault();
                next();
                return;
            }
            if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                setZoom((z) => (z > 1 ? 1 : 2.4));
                setDrag({ x: 0, y: 0 });
                return;
            }
        };

        window.addEventListener("keydown", onKeyDown, { passive: false });

        return () => {
            window.removeEventListener("keydown", onKeyDown as any);
            document.body.style.overflow = prevOverflow;
        };
    }, [next, onClose, prev]);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;

        const update = (): void => {
            setHeaderHeight(el.getBoundingClientRect().height || 0);
        };

        update();

        const ro = new ResizeObserver(() => {
            update();
        });

        ro.observe(el);

        window.addEventListener("resize", update);

        return () => {
            window.removeEventListener("resize", update);
            ro.disconnect();
        };
    }, []);

    const onOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (e.target === overlayRef.current) onClose();
    };

    const pointerRef = useRef<{
        active: boolean;
        lastX: number;
        lastY: number;
        startX: number;
        startY: number;
        moved: boolean;
    }>({
        active: false,
        lastX: 0,
        lastY: 0,
        startX: 0,
        startY: 0,
        moved: false,
    });

    const suppressClickRef = useRef<boolean>(false);

    const onImagePointerDown = (
        e: React.PointerEvent<HTMLImageElement>,
    ): void => {
        (e.currentTarget as HTMLImageElement).setPointerCapture(e.pointerId);
        pointerRef.current.active = true;
        pointerRef.current.lastX = e.clientX;
        pointerRef.current.lastY = e.clientY;
        pointerRef.current.startX = e.clientX;
        pointerRef.current.startY = e.clientY;
        pointerRef.current.moved = false;
        suppressClickRef.current = false;
        setIsDragging(true);
    };

    const onImagePointerMove = (
        e: React.PointerEvent<HTMLImageElement>,
    ): void => {
        if (!pointerRef.current.active) return;

        const dx: number = e.clientX - pointerRef.current.lastX;
        const dy: number = e.clientY - pointerRef.current.lastY;

        pointerRef.current.lastX = e.clientX;
        pointerRef.current.lastY = e.clientY;

        if (zoom > 1) {
            setDrag((d) => ({ x: d.x + dx, y: d.y + dy }));
        }
    };

    const onImagePointerUp = (
        e: React.PointerEvent<HTMLImageElement>,
    ): void => {
        if (!pointerRef.current.active) return;

        pointerRef.current.active = false;
        setIsDragging(false);

        if (zoom > 1) return;

        const totalDx: number = e.clientX - pointerRef.current.startX;
        const totalDy: number = e.clientY - pointerRef.current.startY;

        const absX: number = Math.abs(totalDx);
        const absY: number = Math.abs(totalDy);

        if (absX < 40 || absX < absY) return;

        if (totalDx > 0) prev();
        else next();
    };

    const toggleZoom = useCallback((): void => {
        setZoom((z) => (z > 1 ? 1 : 2.4));
        setDrag({ x: 0, y: 0 });
    }, []);

    const wrapperStyle: React.CSSProperties = useMemo(() => {
        const tx = zoom > 1 ? drag.x : 0;
        const ty = zoom > 1 ? drag.y : 0;
        return {
            transform: `translate3d(${tx}px, ${ty}px, 0) scale(${zoom})`,
            transformOrigin: "top center",
            transition: isDragging ? "none" : "transform 140ms ease-out",
            willChange: "transform",
        };
    }, [drag.x, drag.y, isDragging, zoom]);

    const imgStyle: React.CSSProperties = {
        cursor: zoom > 1 ? "grab" : "zoom-in",
        touchAction: "none",
    };

    const arrowBtnClass: string =
        "pointer-events-auto inline-flex items-center justify-center " +
        "rounded-2xl border border-[rgb(var(--border)/0.12)] " +
        "bg-[rgb(var(--accent)/0.08)] text-[rgb(var(--accent-fg))] " +
        "shadow-sm backdrop-blur-md transition " +
        "hover:bg-[rgb(var(--accent)/0.14)] active:scale-[0.985] " +
        "sm:border-[rgb(var(--border)/0.22)] " +
        "sm:bg-[rgb(var(--surface)/0.28)] sm:hover:bg-[rgb(var(--surface)/0.38)]";

    const mediaAreaStyle: React.CSSProperties = useMemo(() => {
        const h: number = Math.max(0, headerHeight);
        return {
            height: `calc(100dvh - ${h}px)`,
        };
    }, [headerHeight]);

    const computeContainInset = (
        containerW: number,
        containerH: number,
        mediaW: number,
        mediaH: number,
        pad: number,
    ): LabelInset => {
        if (
            !Number.isFinite(containerW) ||
            !Number.isFinite(containerH) ||
            !Number.isFinite(mediaW) ||
            !Number.isFinite(mediaH) ||
            containerW <= 0 ||
            containerH <= 0 ||
            mediaW <= 0 ||
            mediaH <= 0
        ) {
            return { left: pad, top: pad };
        }

        const scale = Math.min(containerW / mediaW, containerH / mediaH);
        const dispW = mediaW * scale;
        const dispH = mediaH * scale;

        const offX = (containerW - dispW) / 2;
        const offY = (containerH - dispH) / 2;

        return {
            left: Math.max(pad, offX + pad),
            top: Math.max(pad, offY + pad),
        };
    };

    const updateLabelInset = useCallback((): void => {
        const pad: number = 10;

        const box = mediaBoxRef.current;
        const img = imgRef.current;

        if (!box || !img) {
            setLabelInset({ left: pad, top: pad });
            return;
        }

        const boxRect = box.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();

        if (
            Number.isFinite(boxRect.width) &&
            Number.isFinite(boxRect.height) &&
            Number.isFinite(imgRect.width) &&
            Number.isFinite(imgRect.height) &&
            boxRect.width > 0 &&
            boxRect.height > 0 &&
            imgRect.width > 0 &&
            imgRect.height > 0
        ) {
            const left = Math.max(pad, imgRect.left - boxRect.left + pad);
            const top = Math.max(pad, imgRect.top - boxRect.top + pad);

            setLabelInset({ left, top });
            return;
        }

        const cw = box.clientWidth;
        const ch = box.clientHeight;

        const mw = img.naturalWidth;
        const mh = img.naturalHeight;

        setLabelInset(computeContainInset(cw, ch, mw, mh, pad));
    }, []);

    useEffect(() => {
        const raf = requestAnimationFrame(() => {
            updateLabelInset();
        });

        const box = mediaBoxRef.current;
        if (!box) {
            cancelAnimationFrame(raf);
            return;
        }

        const ro = new ResizeObserver(() => {
            requestAnimationFrame(() => {
                updateLabelInset();
            });
        });

        ro.observe(box);

        const onResize = (): void => {
            requestAnimationFrame(() => {
                updateLabelInset();
            });
        };

        window.addEventListener("resize", onResize);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
            ro.disconnect();
        };
    }, [currentUrl, updateLabelInset, zoom, drag.x, drag.y]);

    return (
        <div
            ref={overlayRef}
            className="fixed inset-0 z-[70] flex flex-col bg-black/80 backdrop-blur-md"
            onMouseDown={onOverlayMouseDown}
            role="dialog"
            aria-modal="true"
            aria-label="Gallery viewer"
        >
            <div
                ref={headerRef}
                className="flex items-center justify-between gap-3 px-3 py-3 sm:px-5"
            >
                <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-white/90">
                        {title ?? "Gallery"}
                    </div>
                    <div className="text-xs text-white/60">
                        {urls.length
                            ? `${index + 1} / ${urls.length}`
                            : "0 / 0"}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={toggleZoom}
                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold text-white/90 transition hover:bg-white/15"
                    >
                        {zoom > 1 ? "Reset" : "Zoom"}
                    </button>

                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-semibold text-white/90 transition hover:bg-white/15"
                    >
                        Close
                    </button>
                </div>
            </div>

            <div
                className="relative min-h-0 overflow-hidden px-3 pb-6 pt-2 sm:px-6"
                style={mediaAreaStyle}
            >
                {currentUrl ? (
                    <div className="w-full max-w-[1200px] mx-auto relative h-full">
                        <div
                            ref={mediaBoxRef}
                            className="relative flex h-full w-full items-center justify-center"
                        >
                            <div style={wrapperStyle}>
                                <img
                                    ref={imgRef}
                                    src={currentUrl}
                                    alt=""
                                    draggable={false}
                                    onLoad={() => {
                                        updateLabelInset();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (suppressClickRef.current) {
                                            suppressClickRef.current = false;
                                            return;
                                        }
                                        toggleZoom();
                                    }}
                                    onPointerDown={onImagePointerDown}
                                    onPointerMove={onImagePointerMove}
                                    onPointerUp={onImagePointerUp}
                                    onPointerCancel={onImagePointerUp}
                                    className="max-h-full max-w-full select-none rounded-2xl object-contain"
                                    style={imgStyle}
                                />
                            </div>

                            <div
                                className="pointer-events-none absolute z-10"
                                style={{
                                    left: labelInset.left,
                                    top: labelInset.top,
                                }}
                            >
                                <div className="w-fit max-w-[85vw] truncate rounded-2xl border border-[rgb(var(--border)/0.14)] bg-[rgb(var(--panel)/0.55)] px-3 py-2 text-xs font-semibold text-[rgb(var(--fg))] shadow-sm backdrop-blur">
                                    {currentLabel}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {canNavigate ? (
                    <>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                prev();
                            }}
                            className={`absolute left-3 top-1/2 hidden -translate-y-1/2 sm:inline-flex ${arrowBtnClass}`}
                            style={{ width: 44, height: 64 }}
                            aria-label="Previous image"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                aria-hidden="true"
                                className="drop-shadow-sm"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>

                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                next();
                            }}
                            className={`absolute right-3 top-1/2 hidden -translate-y-1/2 sm:inline-flex ${arrowBtnClass}`}
                            style={{ width: 44, height: 64 }}
                            aria-label="Next image"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                aria-hidden="true"
                                className="drop-shadow-sm"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </>
                ) : null}
            </div>
        </div>
    );
}
